import * as Sentry from '@sentry/react';
import axios, { AxiosPromise, AxiosRequestConfig } from 'axios';
import { get as lodashGet } from 'lodash';
//import * as path from 'path';
import { eForceUpdate } from 'src/constrains/SalesChannelMultiLanguageSelect';
import { eForceUpdate as styleForceUpdate } from 'src/constrains/SalesChannelStyleSelect/hooks';
import emit from 'src/tools/event';
import fetchJson from 'src/tools/fetchJson';
import { EInterceptorReject } from 'src/types/common/resources';
import { modal } from 'src/utils/Modal';
import history from '../router/history';
import { EnumSalesChannelMultiLanguage, IParam } from '../types/server/params';
import {
  checkIsLogin,
  getLoginDomain,
  getServerUrl,
  getUserInfo,
  updateTimeStamp,
} from './getUserInfo';
import { btoa, generatorAuth, showWarning } from './helper';
import {
  getCurrentLanguage,
  getCurrentSize,
  getCurrentStyle,
  getI18nFromGeneral,
  getLangFromLocalStorage,
} from './tools';
import { EnumSalesChannelMultiCurrency } from 'src/types/server/params';

const base1 = () => `${getServerUrl()}/`; // process.env.REACT_BASE_URL1;
const base2 = process.env.REACT_BASE_URL2; //for config server->login

const getLoginUrl = () => {
  return fetchJson(`${base2}/config/loginDomain`);
};

export const getBaseUrl = () => {
  const HOST = base1();

  // const HOST = process.env.REACT_BASE_URL;
  return HOST;
};

const serves = axios.create({
  //  baseURL: getBaseUrl(),
  timeout: 55000, // 请求过期时间
});

const CancelToken = axios.CancelToken;
const sources = {};
const requestList = []; //重复请求列表

const generateName = (config) => {
  return (
    JSON.stringify(config.method) +
    JSON.stringify(config.url) +
    JSON.stringify(config.data) +
    JSON.stringify(config.params)
  );
};

serves.interceptors.request.use(
  (config: AxiosRequestConfig & IParam) => {
    const { url } = config;
    if (/\/config/.test(url)) {
      config.url = base2 + url;
    } else {
      config.url = base1() + url;
    }
    //Sales Channel 多语言
    handleOnRequest(config);

    const request = config.requestSourceID || generateName(config);

    config.cancelToken = new CancelToken((cancel) => {
      // console.log('sources', sources);
      // console.log('request', request);
      // console.log(' sources[request]', sources[request]);
      sources[request] = cancel;
    });
    if (requestList.includes(request)) {
      // console.log('request', request);

      // console.warn('取消重复请求');
      sources[request](request);
    } else {
      if (!config.multipleRequests) {
        requestList.push(request);
      }
      //2.请求开始，改变loading状态供加载动画使用
      //store.dispatch('changeGlobalState', { loading: true });
    }
    const userInfo = getUserInfo();

    const { isGuest, headers } = config;

    if (!isGuest) {
      const isLogin = checkIsLogin();
      //console.log('isLogin', isLogin);
      if (!isLogin) {
        const domain = getLoginDomain();
        modal.forceError({
          title: 'Auth Error',
          content: 'Session Expired , Please Login',
          onCancel: () => {
            window?.location.replace(`${domain}/login`);
          },
          onCancelText: 'Confirm',
        });
      } else {
        updateTimeStamp();
      }
    }
    if (!isGuest && /\/config/.test(url)) {
      config.headers['Authorization'] = `Basic ${btoa(
        `${lodashGet(userInfo, 'userUuid')}:${lodashGet(
          userInfo,
          'configToken',
        )}`,
      )}`;
    } else if (!isGuest) {
      const auth = generatorAuth();
      config.headers['Authorization'] = auth;
    }
    config.headers = { ...config.headers, ...(headers || {}) };
    // console.log(rest);
    // Object.keys(rest || {}).forEach((i) => {
    //   config[i] = rest[i];
    // });
    return config;
  },
  (error) => {
    console.warn('serve interceptors.request error', error);
    return Promise.reject(error);
  },
);

serves.interceptors.response.use(
  (res: any) => {
    const request = res.config.requestSourceID || generateName(res.config);
    const afterSuccessCb = res.config.afterSuccessCb;
    if (afterSuccessCb) {
      afterSuccessCb();
    }
    const afterSuccessEvent = res.config.afterSuccessEvent || [];
    eventDispatch(afterSuccessEvent);
    handleOnResponse(res);

    requestList.splice(
      requestList.findIndex((item) => item === request),
      1,
    );

    // 2.当请求列表为空时，更改loading状态
    // if (requestList.length === 0) {
    //   store.dispatch('changeGlobalState', { loading: false });
    // }

    const { status, data, config } = res;
    const { returnWholeData, isGql } = config || {};
    const { handleErrorAsResolve, customHandleError } = config || {};

    if (isGql) {
      //console.log('res,isGql', res);
      // const errors = lodashGet(data, 'errors');
      const error = lodashGet(data, 'errors.0');
      const errors = lodashGet(data, 'errors');
      const response = lodashGet(data, 'data');

      if (error) {
        if (handleErrorAsResolve) {
          return Promise.resolve(data);
        }
        if (customHandleError) {
          return Promise.reject(errors);
        }
        const extensions = lodashGet(error, 'extensions.classification');
        const message = lodashGet(error, 'message');
        //serverUnexpectedErrorHandle_client(`${extensions}:${message}`);
        showWarning(`${extensions}:${message}`, () => {
          console.log(`${extensions}:${message}`);
        });
        // const response = lodashGet(data, 'data');
        //return Promise.reject(response);
      } else {
        return Promise.resolve(response);
      }
    }

    if (status != 200) {
      showWarning('System Error', () => {
        console.log('System Error');
      });
    } else {
      if (returnWholeData) {
        return Promise.resolve(res);
      } else {
        const { payload } = data || {};
        return Promise.resolve(payload);
      }
    }
  },
  (error) => {
    const isArrBuffer =
      lodashGet(error, 'config.responseType') === 'arraybuffer';
    const domain = getLoginDomain();
    const is403 = () => {
      modal.forceError({
        title: 'Auth Error',
        content: errorDesc || 'Please Login',
        onCancel: () => {
          if (domain) {
            window?.location.replace(`${domain}/login`);
          } else {
            getLoginUrl().then((res) => {
              const url = lodashGet(res, 'payload');
              window?.location.replace(`${url}/login`);
            });
          }
        },
        onCancelText: 'Confirm',
      });

      return Promise.reject(returnData);
    };
    //history
    const is406 = () => {
      modal.forceError({
        title: '406 Auth Error',
        content: errorDesc || 'Please Login',
        onCancel: () => {
          history.goBack();
        },
        onCancelText: 'Confirm',
      });

      return Promise.reject(returnData);
    };
    const is401 = () => {
      modal.forceError({
        title: '401 Auth Error',
        content: errorDesc || 'Please Login',
        onCancel: () => {
          if (domain) {
            window?.location.replace(`${domain}/login`);
          } else {
            getLoginUrl().then((res) => {
              const url = lodashGet(res, 'payload');
              window?.location.replace(`${url}/login`);
            });
          }
        },
        onCancelText: 'Confirm',
      });

      return Promise.reject(returnData);
    };
    //console.log('error', error);
    if (axios.isCancel(error)) {
      if (error.message === EInterceptorReject.reject) {
        is403();
      }
      // console.log('error', error, error.message);
      //  store.dispatch('changeGlobalState', { loading: false });
      //console.log(123);
      //console.log('cancelled', error);
      // const err = new Error(`Request has been cancelled ${returnData.errMsg}`);
      // err.name = returnData.code;
      const err = new Error(error.toString());
      err.name = `Request is cancelled`;
      Sentry.captureException(err);

      throw new axios.Cancel(`cancel ${error}`);
    }
    requestList.length = 0;
    let d = {};
    const { config, data = {}, status } = error.response || {};

    try {
      const _data = isArrBuffer
        ? JSON.parse(String.fromCharCode.apply(null, new Uint8Array(data)))
        : null; // JSON.parse(new TextDecoder().decode(data));
      d = _data || data || {};
    } catch (e) {
      d = data || {};
    }
    // Obviosuly replace this with whatever your array buffer is

    const { errorDesc, errorCode } = lodashGet(d, 'payload') || {
      errorDesc: '',
      errorCode: '',
    };
    const { handleErrorAsResolve, customHandleError } = config || {};

    const { url, data: body, method } = config || {};

    const returnData = {
      code: status || 'no code',
      errMsg: errorDesc || 'no code',
      errorCode: errorCode || 'no errorCode',
      __requestRejected: true,
      entireErrorResponse: JSON.stringify(error.response || {}),
      entireError: JSON.stringify(error || {}),
    };
    if (handleErrorAsResolve) {
      return Promise.resolve(returnData);
    } else {
      if (status == 403) {
        is403();
      }
      if (status == 401) {
        is401();
      }
      if (status === 406) {
        is406();
      }
      if (!customHandleError) {
        modal.error({
          title: getI18nFromGeneral('general.tools_server.requestError'),
          content: errorDesc || 'System Error, Please Contact Admin',
          onCancel: () => {
            console.error(errorDesc || 'System Error, Please Contact Admin');
          },
          onCancelText: 'Ok',
        });
      }

      const err = new Error(
        `code:${returnData.code} errorCode:${returnData.errorCode} message:${returnData.errMsg} method:${method} body:${body} entireErrorResponse:${returnData.entireErrorResponse} entireError:${returnData.entireError}`,
      );
      err.name = `Network issue ${returnData.code} ${url}`;
      if (status != 403) {
        Sentry.captureException(err);
      }

      return Promise.reject(returnData);
    }
  },
);

const post = <T>(
  url: string,
  body: { [propName: string]: any },
  config: IParam & AxiosRequestConfig,
): Promise<T> => {
  return serves.post(url, body, config);
};
const remove = <T>(
  url: string,
  config: IParam & AxiosRequestConfig,
): Promise<T> => {
  return serves.delete(url, config);
};
const get = <T>(
  url: string,
  config: IParam & AxiosRequestConfig,
): Promise<T> => {
  return serves.get(url, config);
};

const cancelRequest = (idArr: string[]) => {
  idArr.forEach((item) => {
    if (sources[item]) {
      try {
        sources[item]('取消前页面请求');
      } catch (e) {}
    }
  });
  idArr.forEach((item) => {
    if (sources[item]) {
      try {
        delete sources[item];
      } catch (e) {}
    }
  });
};

const cancelAllRequests = () => {
  Object.keys(sources).forEach((item) => {
    if (sources[item]) {
      try {
        sources[item]('取消前页面所有请求');
      } catch (e) {}
    }
  });
};
// router.beforeEach((to, from, next) => {
//   document.title = to.meta.title || to.name;
//   //路由发生变化时取消当前页面网络请求
//   Object.keys(sources).forEach((item) => {
//     sources[item]('取消前页面请求');
//   });
//   for (var key in sources) {
//     delete sources[key];
//   }
//   next();
// });

const responseHandler = (res) => {
  const { data, code, errMsg, errorCode, status } = res || {};
  if (status === 200 && data) {
    const { payload } = data;
    return {
      data: payload,
      success: true,
    };
  } //dui
  if (code && errorCode) {
    if (code === 400) {
      return {
        success: false,
        errorCode,
        errMsg,
      };
    }
    if (code === 401) {
      modal.forceError({
        title: '401 Auth Error',
        content: 'System Error, Please Contact Admin',
        onCancel: () => {
          //history.push('/login');
        },
        onCancelText: 'Confirm',
      });
      return {
        success: false,
        errorCode,
        errMsg,
      };
    }
    if (code === 403) {
      modal.forceError({
        title: '403 Auth Error',
        content: 'System Error, Please Contact Admin',
        onCancel: () => {
          history.push('/login');
        },
        onCancelText: 'Confirm',
      });
      return {
        success: false,
      };
    }
    modal.error({
      title: getI18nFromGeneral('general.tools_server.requestError'),
      content: errMsg || 'System Error, Please Contact Admin',
      onCancel: () => {
        console.error(errMsg || 'System Error, Please Contact Admin');
      },
      onCancelText: 'Ok',
    });
    return {
      success: false,
    };
  }
};
const printCallback = (request) =>
  request
    .then((res) => {
      const blob = new Blob([res.data], {
        type: 'application/pdf',
      });
      return blob;
    })
    .then((response) => {
      //调用打印接口
      const data = URL.createObjectURL(response);
      const iframe = document.createElement('iframe');
      iframe.className = 'tmp-pdf';
      iframe.style.display = 'none';
      iframe.src = data;
      document.body.appendChild(iframe);
      setTimeout(function () {
        iframe.contentWindow.print();
        URL.revokeObjectURL(data);
      }, 100);
      window.onerror = function myErrorHandler(errorMsg, url, lineNumber) {
        alert('error, pls contact admin'); //or any message
      };
    })
    .catch((err) => {
      modal.error({
        title: getI18nFromGeneral('general.tools_server.requestError'),
        content: err || 'Print Error, Please Contact Admin',
        onCancel: () => {
          console.error(err || 'Print Error, Please Contact Admin');
        },
        onCancelText: 'Ok',
      });
    });

const print = {
  get: (url, config?) => printCallback(serves.get(url, config)),
  post: (url, body, config?) => printCallback(serves.post(url, body, config)),
};

// const Print = (
//   url: string,
//   body?: { [propName: string]: any },
//   config?: IParam & AxiosRequestConfig,
// ) => {
//   return serves
//     .post(url, body, config)
//     .then((res) => {
//       const blob = new Blob([res.data], {
//         type: 'application/pdf',
//       });
//       return blob;
//     })
//     .then((response) => {
//       //调用打印接口
//       const data = URL.createObjectURL(response);
//       const iframe = document.createElement('iframe');
//       iframe.className = 'tmp-pdf';
//       iframe.style.display = 'none';
//       iframe.src = data;
//       document.body.appendChild(iframe);
//       setTimeout(function () {
//         iframe.contentWindow.print();
//         URL.revokeObjectURL(data);
//       }, 100);
//       window.onerror = function myErrorHandler(errorMsg, url, lineNumber) {
//         alert('error, pls contact admin'); //or any message
//       };
//     })
//     .catch((err) => {
//       modal.error({
//         title: 'Request Error',
//         content: err || 'Print Error, Please Contact Admin',
//         onCancel: () => {
//           console.error(err || 'Print Error, Please Contact Admin');
//         },
//         onCancelText: 'Ok',
//       });
//     });
// };

export {
  AxiosPromise,
  cancelAllRequests,
  cancelRequest,
  get,
  post,
  print,
  remove,
  responseHandler,
  serves,
  sources,
};

//functions
// export const getCurrentLanguage = () => {
//   const state = getState();
//   const language = currentSelectedLanguage()(state);
//   const primaryLanguage = currentPrimaryLanguage()(state);
//   return { language, primaryLanguage };
// };

// const getCurrentStyle = () => {
//   const state = getState();
//   const style = currentSelectedStyle()(state);
//   return { style };
// };

// const getCurrentSize = () => {
//   const state = getState();
//   const size = currentSelectedSize()(state);
//   return { size };
// };

const handleSalesChannelMultiLanguageInRequest = (config) => {
  if (config[EnumSalesChannelMultiLanguage.multiLanguageUsePrimaryLanguage]) {
    const { primaryLanguage } = getCurrentLanguage();
    config.params = {
      ...(config.params || {}),
      primaryLocale: primaryLanguage,
      selectedLocale: primaryLanguage,
    };
  } else {
    if (config[EnumSalesChannelMultiLanguage.multiLanguageInParams]) {
      const { language, primaryLanguage } = getCurrentLanguage();
      config.params = {
        ...(config.params || {}),
        primaryLocale: primaryLanguage,
        selectedLocale: language,
      };
    }
  }
};

const handleI18nInRequest = (config) => {
  const lang = getLangFromLocalStorage();
  try {
    const langInJSON = JSON.parse(lang);
    config.params = {
      ...(config.params || {}),
      language: langInJSON,
    };
  } catch (e) {
    return;
  }
};

const handleSalesChannelSiteStyleInRequest = (config) => {
  if (config[EnumSalesChannelMultiLanguage.siteStyleInParams]) {
    const { style } = getCurrentStyle();
    config.params = {
      ...(config.params || {}),
      siteStyle: style,
    };
  }
};

const handleSalesChannelSiteSizeInRequest = (config) => {
  if (config[EnumSalesChannelMultiLanguage.siteStyleSizeInParams]) {
    const { size } = getCurrentSize();
    config.params = {
      ...(config.params || {}),
      breakpoint: size,
    };
  }
};

const handleSalesChannelMultiLanguageInResponse = (res) => {
  const config = res.config;
  if (config[EnumSalesChannelMultiLanguage.multiLanguageForceUpdate]) {
    eForceUpdate();
  }
};

const handleSalesChannelSiteStyleInResponse = (res) => {
  const config = res.config;
  if (config[EnumSalesChannelMultiLanguage.siteStyleForceUpdate]) {
    styleForceUpdate();
  }
};

const handleSalesChannelMultiCurrencyInRequest = (config) => {
  if (config[EnumSalesChannelMultiCurrency.multiCurrencyInParams]) {
    const currencyCode = lodashGet(config, 'data.currencyCode');
    config.params = { ...(config.params || {}), currency: currencyCode };
  }
};

const handleOnRequest = (config) => {
  handleI18nInRequest(config);
  handleSalesChannelMultiLanguageInRequest(config);
  handleSalesChannelSiteStyleInRequest(config);
  handleSalesChannelSiteSizeInRequest(config);
  handleSalesChannelMultiCurrencyInRequest(config);
};

const handleOnResponse = (res) => {
  handleSalesChannelMultiLanguageInResponse(res);
  handleSalesChannelSiteStyleInResponse(res);
};

const eventDispatch = (events) => {
  events.forEach((i) => {
    const { eventKey, ...args } = i;
    emit.emit(eventKey, ...args);
  });
};
